import type { RunLogEntry } from "@/lib/jobs-types";

// Pure timing rules for replaying a finished run's log at its recorded pace (sc-114198). No React, no
// timers and no clock: every function takes plain values, so the player hook owns the one pending timer
// and the component test drives it with fake timers.
//
// A schedule is in RECORDED time after compression, independent of speed: which gaps are compressed never
// changes with the speed control, and speed only divides the waits. At 10× a compressed gap plays in 1 s
// and is still marked.

/** The playback speeds, in display order. */
export const REPLAY_SPEEDS = [1, 2, 5, 10] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];
/** A recorded gap longer than this (strictly) waits this long instead and is marked `⋯ N s`. */
export const REPLAY_GAP_CAP_MS = 10_000;

export interface ReplayStep {
  /** Recorded time, after compression, at which this entry appears. */
  atMs: number;
  /** Whole seconds of the recorded gap before this entry when it was compressed; otherwise null. */
  gapSeconds: number | null;
}

export interface ReplaySchedule {
  steps: ReplayStep[];
  /** The last step's `atMs`, or 0 for an empty log. */
  totalMs: number;
}

/**
 * One step per entry, in the entries' order. The first entry is at 0. Each later step waits the recorded
 * delta from its predecessor; a non-finite or negative delta (an unparseable stamp, an out-of-order pair)
 * waits 0, and a delta over `gapCapMs` waits `gapCapMs` and records the real gap in whole seconds.
 */
export function buildReplaySchedule(
  entries: readonly Pick<RunLogEntry, "logged_at">[],
  gapCapMs: number = REPLAY_GAP_CAP_MS
): ReplaySchedule {
  const steps: ReplayStep[] = [];
  let atMs = 0;
  let previousStamp = Number.NaN;

  entries.forEach((entry, index) => {
    const stamp = Date.parse(entry.logged_at);
    if (index === 0) {
      steps.push({ atMs: 0, gapSeconds: null });
    } else {
      const delta = stamp - previousStamp;
      const raw = Number.isFinite(delta) && delta > 0 ? delta : 0;
      const compressed = raw > gapCapMs;
      atMs += compressed ? gapCapMs : raw;
      steps.push({ atMs, gapSeconds: compressed ? Math.floor(raw / 1000) : null });
    }
    previousStamp = stamp;
  });

  return { steps, totalMs: steps.length ? steps[steps.length - 1].atMs : 0 };
}

/** The number of steps with `atMs ≤ playheadMs` (the steps are in non-decreasing `atMs` order). */
export function shownCount(schedule: ReplaySchedule, playheadMs: number): number {
  let low = 0;
  let high = schedule.steps.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (schedule.steps[mid].atMs <= playheadMs) low = mid + 1;
    else high = mid;
  }
  return low;
}

/** Wall-clock ms until the next unshown step at `speed`, or null once every step is shown. */
export function nextDelayMs(
  schedule: ReplaySchedule,
  playheadMs: number,
  speed: ReplaySpeed
): number | null {
  const shown = shownCount(schedule, playheadMs);
  if (shown >= schedule.steps.length) return null;
  return (schedule.steps[shown].atMs - playheadMs) / speed;
}

/** The playhead after `elapsedWallMs` of playback at `speed`: never past the end, never backwards. */
export function playheadAfter(
  schedule: ReplaySchedule,
  playheadMs: number,
  elapsedWallMs: number,
  speed: ReplaySpeed
): number {
  return Math.min(schedule.totalMs, playheadMs + Math.max(0, elapsedWallMs) * speed);
}
